import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Button } from 'components/shared/Button';
import { Loading } from 'components/shared/Loading';
import { Typography } from 'components/shared/Typography';

import { SessionVerdict } from '../SessionVerdict';
import { ScreenFlowBar } from '../ScreenFlowBar';
import { SessionEventLog } from '../SessionEventLog';
import { SessionStory } from '../SessionStory';

import {
  SESSION_DETAIL_ELEMENT_ID,
  formatDuration,
  formatRelativeDayTitle,
  formatSessionTime,
  getSessionEnvDetails,
  getSessionVerdict,
  type AnalyticsStudentSessionDetailDto,
  type JourneyHighlight,
  type SessionSummary,
} from '../../TimelineSection.helpers';
import { buildChapterScreens } from '../../journeyEventLog';
import { useJourneyTechMode } from '../../journeyTechMode';

import styles from './SessionDetail.module.scss';

const SessionDetail = ({
  loading,
  error,
  session,
  detail,
  highlight,
  studentEmail,
  onCauseEventJump,
  onScreenJump,
  onRetry,
}: {
  loading: boolean;
  error: boolean;
  session: SessionSummary | null;
  detail: AnalyticsStudentSessionDetailDto | null;
  highlight: JourneyHighlight | null;
  studentEmail?: string | null;
  onCauseEventJump: (eventIndex: number) => void;
  onScreenJump: (screenIndex: number) => void;
  onRetry: () => void;
}) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const techMode = useJourneyTechMode();
  const verdict = useMemo(
    () => (detail ? getSessionVerdict(detail, studentEmail) : null),
    [detail, studentEmail, locale],
  );
  /* Same merge as the log's chapters, so every segment jumps to a chapter. */
  const flowScreens = useMemo(
    () => (detail ? buildChapterScreens(detail.screens) : []),
    [detail],
  );

  if (!session) {
    return (
      <section className={styles.panel} id={SESSION_DETAIL_ELEMENT_ID}>
        <Typography className={styles.placeholder} variant="body2">
          {getTranslation('student_page.journey.detail.select_hint')}
        </Typography>
      </section>
    );
  }

  const env = getSessionEnvDetails(detail ?? session, {
    screensCount: detail?.screens.length,
    techMode,
    locale,
  });
  /* Idle time is not part of the duration: «47 с активности» explains the 30-minute timeout. */
  const titleKey =
    session.exitCause === 'idle'
      ? 'student_page.journey.detail.title_idle'
      : 'student_page.journey.detail.title';

  return (
    <section
      className={styles.panel}
      id={SESSION_DETAIL_ELEMENT_ID}
      aria-live="polite"
    >
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <Typography
              className={styles.eyebrow}
              variant="body3"
              weight="medium"
              mode="compact"
            >
              {getTranslation('student_page.journey.detail.eyebrow', {
                platform: getTranslation(
                  `common.text.${session.platform.toLowerCase()}`,
                ),
              })}
            </Typography>
            <Typography
              className={styles.title}
              variant="subtitle"
              weight="semiBold"
            >
              {getTranslation(titleKey, {
                date: formatRelativeDayTitle(session.startedAt, locale),
                time: formatSessionTime(session.startedAt, locale),
                duration: formatDuration(session.durationMs),
              })}
            </Typography>
          </div>
          <div className={styles.envBlock}>
            {env.summary ? (
              <Typography
                className={styles.envSummary}
                variant="body3"
                weight="medium"
                mode="compact"
              >
                {env.summary}
              </Typography>
            ) : null}
            {env.techItems.length > 0 ? (
              <dl className={styles.env}>
                {env.techItems.map((item) => (
                  <div key={item.key} className={styles.envItem}>
                    <Typography
                      className={styles.envLabel}
                      component="dt"
                      variant="body3"
                      mode="compact"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      className={styles.envValue}
                      component="dd"
                      variant="body3"
                      weight="medium"
                      mode="compact"
                    >
                      {item.value}
                    </Typography>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        </div>
        {detail ? (
          <SessionStory detail={detail} studentEmail={studentEmail} />
        ) : null}
        {verdict ? (
          <SessionVerdict
            verdict={verdict}
            onCauseEventJump={onCauseEventJump}
          />
        ) : null}
      </div>

      {loading ? (
        <div className={styles.state}>
          <Loading />
        </div>
      ) : null}

      {error ? (
        <div className={styles.state}>
          <Typography className={styles.stateText} variant="body2">
            {getTranslation('student_page.journey.detail.loading_error')}
          </Typography>
          <Button
            className={styles.retryButton}
            variant="outlined"
            size="sm"
            onClick={onRetry}
          >
            {getTranslation('student_page.journey.detail.retry')}
          </Button>
        </div>
      ) : null}

      {detail ? (
        <>
          <ScreenFlowBar
            durationMs={detail.durationMs}
            screens={flowScreens}
            onScreenJump={onScreenJump}
          />
          <SessionEventLog
            detail={detail}
            highlight={highlight}
            studentEmail={studentEmail}
          />
        </>
      ) : null}
    </section>
  );
};

export default React.memo(SessionDetail) as any;
